import { getMentorshipGroupAddOnPrice } from "@app/shared/prices";
import { MembershipBillingPlan } from "@app/shared/types";
import { formatTimestamp } from "@app/shared/utils";
import { darken, Paper, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { LIMITED_WIDTH } from "app/styles";
import { displayPrice } from "components/displayPrice";
import { DateTime } from "luxon";

const useStyles = makeStyles((theme) => ({
    root: {
        marginBottom: theme.spacing(2),
        padding: theme.spacing(6),
        borderRadius: theme.borderRadius.default,
        transition: "background-color 300ms ease",
        backgroundColor: theme.palette.background.contrast,
        maxWidth: LIMITED_WIDTH,
    },
    donationHeart: {
        fontSize: "1.5rem",
    },

    priceLine: {
        marginTop: 0,
        marginBottom: theme.spacing(1),
    },

    name: {
        marginTop: 0,
    },
}));

export const BillingPlanExplanation = (props: {
    plan: MembershipBillingPlan;
    donationPrice?: number;
    firstBillDate?: DateTime;
    renewalDate?: DateTime;
    classes?: any;
    nextInvoiceAmount?: number;
}): JSX.Element => {
    const classes = useStyles(props);
    const { plan, donationPrice, firstBillDate, renewalDate, nextInvoiceAmount } = props;

    const { baseAmount, billedEvery, addon } = plan;

    const renderRenewalOrFirstBill = (date: DateTime, isFirst: boolean) => (
        <>
            <Typography variant="body2" color="textSecondary" sx={{ m: 0 }}>
                {isFirst ? "Your first bill will be " : "Your membership renews on "}
                {formatTimestamp(date.toMillis(), {
                    month: "long",
                    day: "numeric",
                })}
            </Typography>
        </>
    );

    const renderPlanName = () => {
        if (nextInvoiceAmount === 0) {
            return "Free Membership";
        }

        const totalPrice = donationPrice ? baseAmount + donationPrice : baseAmount;
        return `You're contributing ${displayPrice(totalPrice)} every ${billedEvery}`;
    };

    const renderContributionBreakdown = (membershipAmount: number, addonAmount: number) => (
        <>
            <Typography className={classes.priceLine} variant="body1">
                <b>{displayPrice(membershipAmount)}</b>
                {" is the contribution you chose."}
            </Typography>
            <Typography className={classes.priceLine} variant="body1">
                <b>{displayPrice(addonAmount)}</b>
                {" is for your mentorship group."}
            </Typography>

            {donationPrice && (
                <Typography className={classes.priceLine} variant="body1">
                    <b>{displayPrice(donationPrice)}</b>
                    {" is for your monthly donation."}
                </Typography>
            )}
        </>
    );

    const renderPrice = () => {
        if (addon) {
            const addonAmount = getMentorshipGroupAddOnPrice(plan.membershipType);
            const membershipAmount = baseAmount - addonAmount;
            return renderContributionBreakdown(membershipAmount, addonAmount);
        }

        return;
    };

    return (
        <Paper elevation={0} className={classes.root} data-testid="billingPlanExplanation">
            <Typography variant="h4" className={classes.name}>
                {renderPlanName()}
            </Typography>
            {renderPrice()}
            {firstBillDate && renderRenewalOrFirstBill(firstBillDate, true)}
            {renewalDate && renderRenewalOrFirstBill(renewalDate, false)}
        </Paper>
    );
};

export default BillingPlanExplanation;
